import { Button, Icon, Link } from "@material-ui/core";
import CalendarRange from "mdi-material-ui/CalendarRange";
import Numeric from "mdi-material-ui/Numeric";
import SortAlphabeticalVariant from "mdi-material-ui/SortAlphabeticalVariant";
import Star from "mdi-material-ui/Star";
import Update from "mdi-material-ui/Update";
import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";

import {
    ActionPermissionsObject,
    IWorkspaceResourceFolder,
    PermissionActionType,
    WorkspacePlanTier,
    WorkspaceResourceFolderType,
    WorkspaceResourceType
} from "common/interfaces";

import WorkspaceController, { WorkspaceControllerState } from "js/controllers/WorkspaceController";
import { Header, Layout, PaneBody, PaneContent, SearchBar, SearchBarSort, SideBar } from "js/react/views/SharedResources/Components";

import getObjectHash from "common/utils/getObjectHash";
import Banner from "js/Components/Banner";
import { Button as AppButton } from "js/Components/Button";
import { Icon as BaiIcon } from "js/Components/Icon";
import { getStaticUrl } from "js/config";
import SharedSlidesPaneController, { SharedSlidesItem, SharedSlidesPaneControllerState } from "js/controllers/SharedSlidesPaneController";
import AppController from "js/core/AppController";
import getLogger, { LogGroup } from "js/core/logger";
import { ds } from "js/core/models/dataService";
import { Slide } from "js/core/models/slide";
import { trackActivity } from "js/core/utilities/utilities";
import withProgressDialog from "js/core/utilities/withProgressDialog";
import { t } from "js/i18n";
import { app } from "js/namespaces";
import { ShowConfirmationDialog, ShowDialog, ShowDialogAsync, ShowErrorDialog, ShowMessageDialog } from "js/react/components/Dialogs/BaseDialog";
import ChangePlanDialog from "js/react/components/Dialogs/ChangePlanDialog";
import { Gap20 } from "js/react/components/Gap";
import { FlexBox } from "js/react/components/LayoutGrid";
import { NoMatchNotice, NoTeamSlideNotice, SharedSlideFolderNotice } from "js/react/components/Notice";
import Spinner from "js/react/components/Spinner";
import { themeColors } from "js/react/sharedStyles";
import { UIPaneResultsContainer } from "js/react/views/AddSlide/Panes/Components/SearchBox";
import { MultiSelectThumbnailGrid } from "js/react/views/AddSlide/Panes/Components/ThumbnailGrid";
import { SharedSlideThumbnail } from "js/react/views/SharedResources/Components/SharedSlideThumbnail";
import { selectedClass, SideBarItem } from "js/react/views/SharedResources/Components/WorkspaceResourcesSidebarHelper";
import { useFolderDragItems } from "js/react/views/SharedResources/hooks/useFolderDragItems";
import { folderDropResourceFromWorkspaceResource, moveResourcesToFolder } from "js/react/views/SharedResources/utils/folderDropHelpers";
import { removeResourcesFromFolder } from "js/react/views/SharedResources/utils/removeResourcesFromFolder";
import TeamSlideDialog from "js/react/views/TeamResources/dialogs/TeamSlideDialog";
import CreateSharedSlideDialog from "js/react/views/UserOptions/dialogs/CreateSharedSlideDialog";
import { _ } from "js/vendor";

const logger = getLogger(LogGroup.TEAMS);
const checkIcon = getStaticUrl("/images/auth/done-check.gif");

const ResultsContainer = styled(UIPaneResultsContainer)`
    margin-top: 0;
    padding: 0;
    flex: 1;
    width: 100%;
    min-width: 0;
    min-height: 0;
    box-sizing: border-box;
    overflow-x: hidden;
    overflow-y: auto;
`;

const AddSlideContainer = styled.div`
    display: flex;
    align-items: flex-end;
    justify-content: center;
    width: 100%;
    background: ${themeColors.lightGray};
    padding-top: 20px;
    z-index: 1000;
`;

const AddSlideButton = styled(Button)`
    background: #2EB4E5 !important;
    color: white !important;
    min-width: 150px !important;
    margin-bottom: 15px !important;
    padding: 10px 20px !important;

    &:disabled {
        background: white !important;
        color: #A9A9A9 !important;
    }
`;

const StatsContainer = styled.div`
    background: #f1f1f1;
    width: 100%;
    padding: 15px;
    font-weight: 600;
    .MuiIcon-root {
        margin-right: 10px;
    }
    & > div + div {
        margin-top: 10px;
    }
`;

const StyledAppButton = styled(AppButton)`
    letter-spacing: 0.5px;
`;

export const ALL_SLIDES_RESOURCE_TYPES = [WorkspaceResourceType.SLIDE, WorkspaceResourceType.SLIDE_TEMPLATE];
export const SHARED_SLIDES_RESOURCE_TYPES = [WorkspaceResourceType.SLIDE];
export const SLIDE_TEMPLATES_RESOURCE_TYPES = [WorkspaceResourceType.SLIDE_TEMPLATE];

type SharedSlidesPaneProps = WorkspaceControllerState & SharedSlidesPaneControllerState & {
    resourceTypes: WorkspaceResourceType[];
    isManageLibrary: boolean;
    onSelectFolder?: (resourceTypes: WorkspaceResourceType[], folderId?: string) => void;
    onSelected?: (items: Pick<SharedSlidesItem, "id" | "libraryItemId" | "name">[]) => void;
    folderId?: string;
};

const contentFilterOptions = [
    { field: "all_content", label: t("All Content") },
    { field: "resource:metadata:name", label: t("Title") },
    { field: "resource:metadata:description", label: t("Description") },
    { field: "slide:text", label: t("Slide Content") },
    { field: "resource:tagsText", label: t("Tags") },
    { field: "slide:templateTitle", label: t("Template") },
];

const sortOptions: SearchBarSort[] = [{
    field: "resource.metadata.name", label: t("Name"), direction: "asc", icon: <SortAlphabeticalVariant />
}, {
    field: "resource.metadata.ratedByUsersCount", label: t("Rating"), direction: "desc", icon: <Star />
}, {
    field: "resource.metadata.usedInPresentationsCount", label: t("Popularity"), direction: "desc", icon: <Numeric />
}, {
    field: "resource.createdAt", label: t("Created At"), direction: "desc", icon: <CalendarRange />
}, {
    field: "resource.modifiedAt", label: t("Modified At"), direction: "desc", icon: <Update />
}];

export function SharedSlidesPane({ resourceTypes: initialResourceTypes, tags, fetching, data, isManageLibrary, resourceFolders, onSelectFolder, onSelected, folderId }: SharedSlidesPaneProps) {
    const [query, setQuery] = useState("");
    const [sort, setSort] = useState<SearchBarSort>({
        field: "resource.metadata.name",
        direction: "asc",
    });
    const [selectedContent, setSelectedContent] = useState("all_content");
    const [selectedTag, setSelectedTag] = useState("all_tags");
    const [selectedFolderId, setSelectedFolderId] = useState<string | null>(folderId ?? null);
    const [selectedItems, setSelectedItems] = useState<Pick<SharedSlidesItem, "id" | "libraryItemId" | "name">[]>([]);
    const {
        draggedItems,
        handleStartDragItems,
        handleDropDragItems,
        clearDragItems,
        getActiveDragItems,
    } = useFolderDragItems<SharedSlidesItem>(item => folderDropResourceFromWorkspaceResource(item.resource));
    const [resourceTypes, setResourceTypes] = useState<WorkspaceResourceType[]>(initialResourceTypes);

    const searchResults = SharedSlidesPaneController.initialized ? SharedSlidesPaneController.legacyData : [];

    useEffect(() => {
        setResourceTypes(initialResourceTypes);
    }, [initialResourceTypes.sort().join()]);

    useEffect(() => {
        setSelectedFolderId(folderId ?? null);
    }, [folderId]);

    const controllerSort = [{
        field: sort.field,
        direction: sort.direction,
    }];

    const getControllerFilters = (nextResourceTypes: WorkspaceResourceType[]) => {
        const filters = [];

        if (nextResourceTypes.length > 0 || selectedFolderId) {
            filters.push({
                field: "resource.resourceType",
                operator: "in",
                // If a folder is selected, we always show all slides and slide templates in that folder
                value: selectedFolderId ? ALL_SLIDES_RESOURCE_TYPES : nextResourceTypes,
            });
        }

        if (selectedTag !== "all_tags") {
            filters.push({
                field: "resource.tags",
                operator: "eq",
                value: selectedTag,
            });
        }

        if (selectedFolderId) {
            filters.push({
                field: "resource.folderIds",
                operator: "eq",
                value: selectedFolderId,
            });
        }

        if (!isManageLibrary) {
            filters.push({
                field: "resource.metadata.isDisabled",
                operator: "ne",
                value: true
            });
        }

        return filters;
    };

    const controllerFilters = useMemo(() => getControllerFilters(resourceTypes), [resourceTypes, selectedTag, selectedFolderId, isManageLibrary]);

    const controllerSearch = useMemo(() => ({
        query,
        fields: selectedContent === "all_content" ? undefined : [selectedContent],
    }), [query, selectedContent]);

    const tagFilterOptions = useMemo(() => [
        { field: "all_tags", label: t("All Tags") },
        ...tags.map(tag => ({
            field: tag.tag,
            label: `${tag.tag} (${tag.count})`,
        })),
    ], [tags]);

    useEffect(() => {
        SharedSlidesPaneController.initialize({
            workspaceId: WorkspaceController.workspaceId,
            resourceTypes: initialResourceTypes,
            sort: controllerSort,
            filters: getControllerFilters(initialResourceTypes),
            search: controllerSearch,
        });

        return () => {
            SharedSlidesPaneController.reset();
        };
    }, [initialResourceTypes.sort().join()]);

    const prevSearchStateRef = useRef({
        query,
        selectedContent,
        selectedTag,
        selectedFolderId,
        resourceTypes,
        sort,
    });

    useEffect(() => {
        if (!SharedSlidesPaneController.initialized) {
            return;
        }

        const prev = prevSearchStateRef.current;

        if (!_.isEqual(prev.sort, sort)) {
            SharedSlidesPaneController.setSort(controllerSort);
        }
        if (
            !_.isEqual(prev.resourceTypes, resourceTypes) ||
            prev.selectedTag !== selectedTag ||
            prev.selectedFolderId !== selectedFolderId
        ) {
            SharedSlidesPaneController.setFilters(controllerFilters);
        }
        if (prev.query !== query || prev.selectedContent !== selectedContent) {
            SharedSlidesPaneController.setSearch(controllerSearch);
        }

        prevSearchStateRef.current = { query, selectedContent, selectedTag, selectedFolderId, resourceTypes, sort };
    }, [query, selectedContent, selectedTag, selectedFolderId, resourceTypes, getObjectHash(sort)]);

    const showTeamSlideDialog = async (item: { id: string }) => {
        await ShowDialogAsync(TeamSlideDialog, {
            slideId: item.id,
        });
    };

    const copySlide = async (
        itemModel: SharedSlidesItem,
        updateProps: Partial<Pick<SharedSlidesItem, "name" | "tags" | "description" | "isDisabled" | "isTemplate">> = {}
    ) => {
        try {
            await withProgressDialog({
                title: t("Copying slide..."),
                action: async () => {
                    const {
                        name,
                        id,
                        tags,
                        description,
                        isDisabled,
                        isTemplate,
                    } = { ...itemModel, ...updateProps };

                    const slide = new Slide({ id });
                    await slide.load();

                    const slideModel = _.cloneDeep(slide.attributes);
                    delete slideModel.id;
                    delete slideModel.libraryItemId;
                    delete slideModel.thumbnails;
                    delete slideModel.presentationId;

                    const newSlide = new Slide(slideModel, {
                        userId: app.userId,
                        autoSync: false,
                    });
                    await newSlide.load();

                    const resource = {
                        resourceId: newSlide.id,
                        resourceType: isTemplate ? WorkspaceResourceType.SLIDE_TEMPLATE : WorkspaceResourceType.SLIDE,
                        tags: tags ?? [],
                        folderIds: [],
                        metadata: {
                            name,
                            description,
                            isDisabled,
                            contentModifiedAt: newSlide.get("modifiedAt"),
                            usedByUsers: [],
                            usedInPresentations: [],
                            usedInPublicPresentations: [],
                            ratedByUsers: [],
                            createdBy: app.user.id,
                            modifiedBy: app.user.id,
                        },
                    };

                    await WorkspaceController.createWorkspaceResource({ resource, defaultPermissions: "shared" });

                    if (selectedFolderId) {
                        await WorkspaceController.updateWorkspaceResourceFolderResources({
                            folderId: selectedFolderId,
                            update: {
                                add: [{
                                    resourceId: resource.resourceId,
                                    resourceType: resource.resourceType,
                                    permissions: {
                                        read: { object: true, permissions: true },
                                        write: { object: false, permissions: false },
                                    },
                                }],
                            },
                        });
                    }
                },
            });

            return true;
        } catch (err) {
            logger.error(err, "[SharedSlidesPane] copySlide failed");

            ShowErrorDialog({
                error: t("An error occurred while copying the slide."),
                message: (err as Error).message,
            });

            return false;
        }
    };

    const handleEditSlide = async (item: { id: string, resource: { resourceType: WorkspaceResourceType } }) => {
        AppController.showSlideEditor({ slideId: item.id, resourceType: item.resource.resourceType });
    };

    const handleCopySlide = async (item: SharedSlidesItem) => {
        if (item.isTemplate) {
            const didCopy = await copySlide(item, {
                isTemplate: false,
                name: t("Copy of {{name}}", { name: item.name }),
            });

            if (didCopy) {
                ShowMessageDialog({
                    dialogTitleProps: {
                        style: { padding: "10px 34px" },
                    },
                    buttonOptions: {
                        color: "blue"
                    },
                    title: t("A copy of this slide is now available to your team as a finished, uneditable Shared Slide."),
                    header: (<img src={checkIcon} width={75} height={75} />),
                });
            }
            return;
        }

        const didCopy = await copySlide(item, {
            isTemplate: true,
            name: t("Copy of {{name}}", { name: item.name }),
            isDisabled: false,
        });

        if (didCopy) {
            ShowMessageDialog({
                dialogTitleProps: {
                    style: { padding: "10px 34px" },
                },
                buttonOptions: {
                    color: "blue"
                },
                title: t("A copy of this slide is now available to your team as an editable Slide Template."),
                header: (<img src={checkIcon} width={75} height={75} />),
            });
        }
    };

    const handleDuplicateSlide = async (item: SharedSlidesItem) => {
        try {
            const slide = new Slide({ id: item.id });
            await slide.load();

            await ShowDialogAsync(CreateSharedSlideDialog, {
                slide,
                presentation: null,
                resourceType: item.isTemplate ? WorkspaceResourceType.SLIDE_TEMPLATE : WorkspaceResourceType.SLIDE,
                folderId: selectedFolderId,
                forceCreateNewSlide: true,
                initialName: t("Copy of {{name}}", { name: item.name }),
                initialDescription: item.description ?? "",
                initialTags: item.tags,
                initialIsDisabled: item.isDisabled,
                dialogTitle: t("Duplicate this team slide"),
                okButtonLabel: t("Duplicate"),
            });
        } catch (err) {
            logger.error(err, "[SharedSlidesPane] duplicate slide failed", { id: item.id });

            ShowErrorDialog({
                error: t("An error occurred while duplicating the slide."),
                message: (err as Error).message,
            });
        }
    };

    const handleRemoveFromFolder = async (item: {
        id: string;
        resource: { resourceId: string; resourceType: WorkspaceResourceType };
    }) => {
        if (!selectedFolderId) {
            return;
        }

        const items = selectedItems.length > 0 && selectedItems.some(selected => selected.id === item.id)
            ? selectedItems
            : [item];
        const resources = items.map(selectedItem => ({
            resourceId: selectedItem.resource.resourceId,
            resourceType: selectedItem.resource.resourceType,
        }));

        await removeResourcesFromFolder({
            folderId: selectedFolderId,
            resources,
            resourceLabel: items.length === 1 ? t("slide") : t("slides"),
        });
    };

    const handleDeleteSlide = async (item: SharedSlidesItem) => {
        const { resource: { resourceId, resourceType } } = item;
        const resourceName = resourceType === WorkspaceResourceType.SLIDE_TEMPLATE
            ? t("Slide Template")
            : t("Shared Slide");
        const usedByCount = item.usedByCount ?? item.resource?.metadata?.usedByUsers?.length ?? 0;

        const deleteResource = async () => {
            try {
                await withProgressDialog({
                    title: t("Deleting {{resourceName}}...", { resourceName }),
                    action: () => WorkspaceController.deleteWorkspaceResource(
                        { resourceId, resourceType },
                        { trackActivity: "SharedSlideLibrary" }
                    ),
                });
            } catch (err) {
                logger.error(err, "[SharedSlidesPane] delete failed", { resourceId, resourceType });

                ShowErrorDialog({
                    error: t("An error occurred while deleting the Team Slide."),
                    message: (
                        <>
                            <p>
                                <strong>{t("Error")}:</strong> {(err as Error).message}
                            </p>
                            <p>{t("We apologize for the inconvenience. Please contact us at support@beautiful.ai.")}</p>
                        </>
                    ),
                });
            }
        };

        if (resourceType === WorkspaceResourceType.SLIDE) {
            if (await ShowConfirmationDialog({
                title: t("Are you sure you want to delete this {{resourceName}}?", { resourceName }),
                message: (
                    <>
                        <span>
                            {t("Deleting a Shared Slide permanently removes it from your Team Resources.")}{" "}
                            {t("Existing instances of this slide will no longer update automatically and will be editable by presentation editors.")}{" "}
                            <Link onClick={() => showTeamSlideDialog(item)}>{t("See where this slide is being used.")}</Link>
                        </span>
                        <Gap20 />
                        <StatsContainer>
                            <FlexBox left middle>
                                <Icon>star</Icon>
                                {t("{{count}} {{likes}}", {
                                    count: item.ratedByCount,
                                    likes: item.ratedByCount === 1 ? t("like") : t("likes"),
                                })}
                            </FlexBox>
                            <FlexBox left middle>
                                <Icon>person</Icon>
                                {t("Used by {{count}} {{people}}", {
                                    count: usedByCount,
                                    people: usedByCount === 1 ? t("person") : t("people"),
                                })}
                            </FlexBox>
                            <FlexBox left middle>
                                <Icon>cloud_download</Icon>
                                {t("Used in {{count}} {{presentations}}", {
                                    count: item.presentationCount,
                                    presentations: item.presentationCount === 1 ? t("presentation") : t("presentations"),
                                })}
                            </FlexBox>
                        </StatsContainer>
                    </>
                ),
                okButtonLabel: t("Delete Shared Slide"),
                buttonOptions: {
                    acceptButtonColor: "red",
                },
            })) {
                await deleteResource();
            }
            return;
        }

        if (await ShowConfirmationDialog({
            title: t("Are you sure you want to delete this {{resourceName}}?", { resourceName }),
            message: t("Deleting a {{resourceName}} permanently removes it from your {{resourceName}} library and prevents it from being used in any future presentations.", { resourceName }),
            buttonOptions: { cancelButtonVariant: "outlined", acceptButtonColor: "red" },
            okButtonLabel: t("PERMANENTLY DELETE TEAM SLIDE"),
        })) {
            await deleteResource();
        }
    };

    const handleFolderDrop = async (folder: IWorkspaceResourceFolder) => {
        const items = getActiveDragItems();
        if (!items.length) {
            return;
        }

        if (folder.id !== selectedFolderId) {
            setSelectedFolderId(folder.id);
            setQuery("");
            onSelectFolder?.(resourceTypes, folder.id);
        }

        clearDragItems();

        await moveResourcesToFolder({
            folder,
            resources: items,
            progressTitle: t("Moving slide to folder..."),
            progressTitlePlural: count => t("Moving {{count}} slides to folder...", { count }),
            errorMessage: t("An error occurred while moving slides to the folder."),
            logTag: "SharedSlidesPane",
        });
    };

    const handleThumbnailVisibilityChange = (isVisible: boolean, item: { resource: { id: string } }) => {
        if (!isVisible || !SharedSlidesPaneController.canLoadMore) {
            return;
        }

        const index = data.findIndex(({ resource }) => resource.id === item.resource.id);
        if (index === data.length - 1) {
            SharedSlidesPaneController.loadMore();
        }
    };

    const handleAddItem = async () => {
        const resourceType = _.isEqual(resourceTypes, SHARED_SLIDES_RESOURCE_TYPES)
            ? WorkspaceResourceType.SLIDE
            : WorkspaceResourceType.SLIDE_TEMPLATE;

        AppController.showSlideEditor({
            resourceType,
            slideId: undefined,
            folderId: selectedFolderId ?? undefined,
        });
    };

    const handleAddSelectedSlides = (items: Pick<SharedSlidesItem, "id" | "libraryItemId" | "name">[] = selectedItems) => {
        if (!onSelected) {
            return;
        }

        const analyticsProps = {
            ...ds.selection.presentation.getAnalytics(),
            slide_ids: items.map(item => item.id),
        };
        trackActivity("Presentation", "LibrarySlideAdded", null, null, analyticsProps);

        onSelected(items);
    };

    const handleSearch = (nextQuery: string) => {
        setQuery(nextQuery);
        setResourceTypes(ALL_SLIDES_RESOURCE_TYPES);
        setSelectedFolderId(null);
        onSelectFolder?.(ALL_SLIDES_RESOURCE_TYPES, null);
    };

    const handleSort = (nextSort: SearchBarSort) => {
        setSort(nextSort);
    };

    const handleContentFilterChange = (event: { target: { value: string } }) => {
        setSelectedContent(event.target.value);
    };

    const handleSelectTag = (event: { target: { value: string } }) => {
        setSelectedTag(event.target.value);
    };

    const handleSelectResourceType = (nextResourceTypes: WorkspaceResourceType[]) => {
        setResourceTypes(nextResourceTypes);
        onSelectFolder?.(nextResourceTypes, null);
        setSelectedFolderId(null);
        if (!_.isEqual(nextResourceTypes, ALL_SLIDES_RESOURCE_TYPES)) {
            setQuery("");
        }
    };

    const canUseSharedResources = WorkspaceController.actionPermissions[ActionPermissionsObject.SHARED_RESOURCES].use;
    const canUpgradeToManageSharedResources = WorkspaceController.canUpgradeToRemoveActionRestriction(
        ActionPermissionsObject.SHARED_RESOURCES,
        PermissionActionType.MANAGE
    );

    const renderSlides = () => {
        const isLoadingMore = fetching && searchResults.length > 0;
        const isLoadingInitial = fetching && searchResults.length === 0;

        if (isLoadingInitial || !SharedSlidesPaneController.initialized) {
            return <Spinner />;
        }

        if (!searchResults.length) {
            if (!query) {
                if (selectedFolderId) {
                    return (<SharedSlideFolderNotice hideMessage={!isManageLibrary} />);
                }

                if (selectedTag === "all_tags" && !selectedFolderId) {
                    if (_.isEqual(resourceTypes, SHARED_SLIDES_RESOURCE_TYPES)) {
                        return (<SharedSlideFolderNotice
                            title={t("You don’t have any Shared Slides yet.")}
                            message={t("Create a slide once, and your team can use it infinitely. Updates will apply anywhere the slide is used.")}
                        />);
                    }
                    if (_.isEqual(resourceTypes, SLIDE_TEMPLATES_RESOURCE_TYPES)) {
                        return (<SharedSlideFolderNotice
                            title={t("You don’t have any Slide Templates yet.")}
                            message={t("Give your team a head start with a custom library of slide templates.")}
                        />);
                    }

                    return <NoTeamSlideNotice />;
                }
            }
            return <NoMatchNotice />;
        }

        return (
            <MultiSelectThumbnailGrid
                responsiveColumns
                colGap={24}
                rowGap={24}
                items={searchResults}
                allowMultiSelect
                allowDragging={isManageLibrary && canUseSharedResources}
                thumbnailClass={SharedSlideThumbnail}
                isLoadingMore={isLoadingMore}
                thumbnailProps={{
                    isManageLibrary,
                    canUpgradeToManageSharedResources,
                    folderId: selectedFolderId,
                    onEditSlide: handleEditSlide,
                    onDeleteSlide: handleDeleteSlide,
                    onRemoveFromFolder: selectedFolderId ? handleRemoveFromFolder : undefined,
                    onCopySlide: handleCopySlide,
                    onDuplicateSlide: handleDuplicateSlide,
                    onVisibleChange: handleThumbnailVisibilityChange,
                }}
                onItemSelected={setSelectedItems}
                onStartDragItems={handleStartDragItems}
                onDropDragItems={handleDropDragItems}
                onDoubleClick={canUpgradeToManageSharedResources ? undefined : (slide => {
                    if (isManageLibrary) {
                        showTeamSlideDialog(slide);
                    } else {
                        handleAddSelectedSlides([slide]);
                    }
                })}
            />
        );
    };

    const title = useMemo(() => {
        if (_.isEqual(resourceTypes, SHARED_SLIDES_RESOURCE_TYPES)) {
            return t("Shared Slides");
        }
        if (_.isEqual(resourceTypes, SLIDE_TEMPLATES_RESOURCE_TYPES)) {
            return t("Slide Templates");
        }
        return t("Team Slides");
    }, [resourceTypes]);

    return (
        <Layout>
            {canUseSharedResources && (<>
                {isManageLibrary && <Header title={title}>
                    <StyledAppButton onClick={handleAddItem} blue large>
                        <BaiIcon white fill>add_circle</BaiIcon>
                        {t("New Slide")}
                    </StyledAppButton>
                </Header>}

                {canUseSharedResources &&
                    <SearchBar
                        query={query}
                        onSearch={handleSearch}
                        searchPlaceholder={t("Search slides...")}
                        contentFilterOptions={contentFilterOptions}
                        selectedContentFilter={selectedContent}
                        onContentFilterChange={handleContentFilterChange}
                        tagFilterOptions={tagFilterOptions}
                        selectedTagFilter={selectedTag}
                        onTagFilterChange={handleSelectTag}
                        sortOptions={sortOptions}
                        sort={sort}
                        onSetSort={handleSort}
                        sortDisabled={query.length > 0}
                    />
                }
            </>)}

            {canUpgradeToManageSharedResources && (
                <FlexBox center fillWidth>
                    <Banner
                        headerText={t("Unlock Team Slides")}
                        bodyText={t("Upgrade to a Team plan to customize a searchable library of reusable templates and Shared Slides that stay synced across every deck.")}
                        buttonLabel={t("Upgrade")}
                        onButtonClick={() => ShowDialog(ChangePlanDialog,
                            {
                                featuredPlan: WorkspacePlanTier.TEAM,
                                analytics: { cta: "team_templates" }
                            })}
                    />
                </FlexBox>
            )}

            <PaneBody>
                <SideBar
                    readOnly={!isManageLibrary}
                    resourceTypes={ALL_SLIDES_RESOURCE_TYPES}
                    folderType={WorkspaceResourceFolderType.SHARED}
                    createResourcesType={WorkspaceResourceType.SLIDE}
                    resourceFolders={resourceFolders}
                    selectedFolderId={selectedFolderId}
                    draggedItems={draggedItems}
                    onFolderDrop={handleFolderDrop}
                    onSelectFolder={(nextFolderId: string | null) => {
                        setSelectedFolderId(nextFolderId);
                        setQuery("");
                        onSelectFolder?.(ALL_SLIDES_RESOURCE_TYPES, nextFolderId ?? undefined);
                    }}
                >
                    <SideBarItem
                        className={selectedClass(_.isEqual(resourceTypes, ALL_SLIDES_RESOURCE_TYPES) && selectedFolderId === null)}
                        onClick={() => handleSelectResourceType(ALL_SLIDES_RESOURCE_TYPES)}
                        id="all-slides"
                    >
                        <Icon>select_all</Icon>
                        {t("All slides")}
                    </SideBarItem>
                    <SideBarItem
                        className={selectedClass(_.isEqual(resourceTypes, SHARED_SLIDES_RESOURCE_TYPES) && selectedFolderId === null)}
                        onClick={() => handleSelectResourceType(SHARED_SLIDES_RESOURCE_TYPES)}
                        id="shared-slides"
                    >
                        <Icon>link</Icon>
                        {t("Shared Slides")}
                    </SideBarItem>
                    <SideBarItem
                        className={selectedClass(_.isEqual(resourceTypes, SLIDE_TEMPLATES_RESOURCE_TYPES) && selectedFolderId === null)}
                        onClick={() => handleSelectResourceType(SLIDE_TEMPLATES_RESOURCE_TYPES)}
                        id="slide-templates"
                    >
                        <Icon>edit_note</Icon>
                        {t("Slide Templates")}
                    </SideBarItem>
                </SideBar>

                <PaneContent>
                    <ResultsContainer>
                        {renderSlides()}
                    </ResultsContainer>
                    {!isManageLibrary && !fetching && SharedSlidesPaneController.legacyData.length > 0 &&
                        <AddSlideContainer>
                            <AddSlideButton disabled={!selectedItems.length} onClick={() => handleAddSelectedSlides()}>
                                {t("Add Selected Slides")}
                            </AddSlideButton>
                        </AddSlideContainer>
                    }
                </PaneContent>
            </PaneBody>
        </Layout>
    );
}

export default WorkspaceController.withInitializedState(SharedSlidesPaneController.withState(SharedSlidesPane));
